import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRightLeft,
  BadgeDollarSign,
  BellRing,
  Check,
  FileText,
  Paperclip,
  Pencil,
  Phone,
  Plus,
  Trash2,
  Undo2,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Timeline, TimelineContent, TimelineIndicator, TimelineItem, TimelineSeparator } from '@/components/reui/timeline';
import { IconTile } from '@/components/reui/icon-tile';
import { Badge } from '@/components/reui/badge';
import { ApiError } from '@/lib/query-client';
import { AvatarPersona } from '@/components/rdt/avatar-persona';
import { CampoDecimal } from '@/components/rdt/campo-decimal';
import { ConfirmDialog, type ConfirmDialogState } from '@/components/rdt/confirm-dialog';
import { PasoPalette } from '@/components/rdt/paso-palette';
import { PasoForm } from '@/components/rdt/paso-forms';
import {
  actividadItems,
  AUTOR_HISTORICO,
  esFechaFutura,
  eventoCampoLabel,
  eventoValor,
  fFecha,
  fHora,
  fmtMonto,
  fmtPct,
  interlocutorLabel,
  itemKey,
  lifecycle,
  perLabel,
  periodoActual,
  proximoPaso,
  tamanoLegible,
  tipoAdjuntoLabel,
  ultimoHitoCompletado,
  ultimosPeriodos,
  type ActividadItem,
  type TipoPaso,
} from '@/components/rdt/rdt-domain';
import {
  adjuntoHref,
  useActualizarComentario,
  useBorrarComentario,
  useBorrarMonto,
  useBorrarReferencia,
  useDeshacerHito,
  useEditarMonto,
  useEditarReferencia,
  type EditarMontoInput,
  type EditarReferenciaInput,
} from '@/hooks/use-rdt-mutations';
import { useMe } from '@/hooks/use-me';
import type { ObraDetalle, RdtDetalle } from '@/types/api';
import type { ActividadListaProps } from '@/components/avivador/panel-avivador-tipos';

type ItemConIcono = Exclude<ActividadItem['t'], 'comentario' | 'creacion'>;

const ICONS: Record<ItemConIcono, LucideIcon> = {
  chequeo: Zap,
  hito: Check,
  contacto: Phone,
  adjunto: Paperclip,
  monto: BadgeDollarSign,
  referencia: FileText,
  evento: ArrowRightLeft,
};

// Tono del IconTile (variant="soft", tal cual c-icon-tile-15: el tono sale
// de currentColor con UNA clase de texto, `text-{x}-foreground dark:text-{x}`
// — mismo patrón que R1 para el ícono del título del form).
const ICON_TINT: Record<ItemConIcono, string> = {
  chequeo: 'text-info-foreground dark:text-info',
  hito: 'text-success-foreground dark:text-success',
  contacto: 'text-muted-foreground',
  adjunto: 'text-muted-foreground',
  monto: 'text-success-foreground dark:text-success',
  referencia: 'text-warning-foreground dark:text-warning',
  evento: 'text-muted-foreground',
};

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message;
  return fallback;
}

function ActividadAvatar({ nombre, fotoUrl }: { nombre: string; fotoUrl: string | null }) {
  return <AvatarPersona nombre={nombre} fotoUrl={fotoUrl} size={22} className="mt-px" />;
}

// El nodo (avatar/IconTile) + la línea conectora del hilo salen de
// @reui/timeline: TimelineIndicator centra el avatar/IconTile y
// TimelineSeparator conecta hasta el próximo indicador — el cálculo de ReUI
// (`calc(100% - alto - gap)` + `translate-y-N`) llega exacto al borde del
// ítem siguiente sin importar el alto de cada fila.
//
// CLASE_INDICADOR/CLASE_SEPARADOR/CLASE_ITEM llevan el modificador COMPLETO
// `group-data-[orientation=vertical]/timeline:` (no solo el valor), igual
// que los defaults del componente (`-left-6`, `ms-8`…): en Tailwind v4 el
// `:where()` generado para el componente envuelve solo `.group/timeline`,
// no el atributo `[data-orientation=vertical]`, así que una clase plana sin
// el modificador completo pierde por especificidad aunque esté después en
// el DOM (hallazgo ALTA, QA de R3, lente `reui-tal-cual`).
//
// Geometría a paridad exacta con la rc.1 (rc.6, qa_visual_reui_2026-09-28.md
// U-3): un solo nodo de 22px (avatar e IconTile, `[--icon-tile-size:22px]
// [--icon-tile-icon-size:12px]`, grep `IconTile` en este archivo) con 9px de
// gap antes del contenido (`ms-[31px]` en CLASE_ITEM, más abajo). `-left-5`
// sale de resolver `M - N - W/2 = 0` (el borde izquierdo del nodo cae en
// x=0 del Timeline) con M=31 (el indent) y W=22 (el nodo): N=31-11=20 →
// `-left-5`, mismo valor para el indicador y el separador. `top-[9px]` (O5
// de la QA del 28/9): el nodo es absoluto y el vendorizado lo deja en
// `top-0`, pero el texto arranca después del `py-[9px]` de TimelineContent;
// sin esto el avatar quedaba ~5px más arriba que el nombre (en la rc.1 nodo
// y texto compartían ese padding). `translate-y-[35px]` (= 9 + 22 + 4) y
// `h-[calc(100%-22px-4px)]` hacen que el separador arranque 4px debajo del
// nodo y llegue exacto al próximo ítem sin importar el alto de la fila. Si
// cambia el `py-[9px]` de TimelineContent, cambian los dos 9.
const CLASE_INDICADOR = 'group-data-[orientation=vertical]/timeline:-left-5 group-data-[orientation=vertical]/timeline:top-[9px] flex size-[22px] items-center justify-center border-none bg-transparent p-0';
// bg-border-subtle (rc.6, qa_visual_reui_2026-09-28.md U-3): el conector es
// siempre gris neutro, paridad con la rc.1 — pisa el `bg-primary/10` que
// trae por defecto el vendorizado (reui/timeline.tsx), mismo classGroup de
// fondo, sin condicionar por `data-completed`.
const CLASE_SEPARADOR =
  'group-data-[orientation=vertical]/timeline:-left-5 group-data-[orientation=vertical]/timeline:h-[calc(100%-22px-4px)] group-data-[orientation=vertical]/timeline:translate-y-[35px] bg-border-subtle';
// `ms-[31px]` pisa el `ms-8` que TimelineItem trae por defecto: reproduce el
// indent de la rc.1 (nodo 22px + gap 9px, ver la nota de arriba para cómo
// entra en la fórmula de `-left-5`). `not-last:pb-0` (rc.6,
// qa_visual_reui_2026-09-28.md U-3/F3) saca el `not-last:pb-6` (24px) que el
// vendorizado suma entre ítems además del propio `py-[9px]` de
// TimelineContent — en la rc.1 ese hueco extra no existía. `flex-none`, a
// diferencia de los dos anteriores, no lleva el modificador `group-data-
// […]/timeline:`: el `flex-1` que reemplaza es incondicional en el
// vendorizado (no depende de la orientación), así que alcanza con la clase
// plana — mismo classGroup de Tailwind (flex-grow/shrink/basis), gana por
// twMerge sin necesitar igualar ningún modificador.
const CLASE_ITEM =
  'flex-none group-data-[orientation=vertical]/timeline:ms-[31px] group-data-[orientation=vertical]/timeline:not-last:pb-0';

export interface ActividadItemRowProps {
  item: ActividadItem;
  rdtId: number;
  rdtNro: string;
  migrada: boolean;
  /** Ya resuelto por el caller (Actividad, más abajo): necesita el `rdt`
   *  completo (autoría real de montos/referencias vía rdt.montos/
   *  rdt.referencias, más el archivado) que este componente no recibe. */
  puedeEditar: boolean;
  puedeBorrar: boolean;
  /** Solo true para el ítem 'hito' que es el ÚLTIMO hito completado del
   *  catálogo (punto 11a v0.9.0) — deshacer es de a uno, orden inverso. */
  puedeDeshacer: boolean;
  /** El `step` del TimelineItem (@reui/timeline, c-timeline-6) — 1-indexado,
   *  el índice del ítem en el hilo. No representa progreso (esto no es un
   *  stepper): solo identifica la fila para el primitivo. */
  step: number;
  /** Ancla del scroll+resaltado desde la lápida/stepper (`act-${itemKey}`,
   *  puntos 8/9/14 v0.9.0). */
  domId: string;
  /** Fase de la animación de resaltado one-shot — null: sin resaltar; 'on':
   *  recién llegado (bg-primary/12 sin transición, primer paint); 'fading':
   *  la clase de bg se retira CON transición, así el navegador anima el
   *  fade de vuelta a transparente (mismo ítem puede re-highlightearse: el
   *  caller lo re-dispara vía `key`/nonce). */
  highlightPhase: 'on' | 'fading' | null;
  /** Sin acciones (editar/borrar/deshacer nunca se muestran, sea cual sea
   *  puedeEditar/puedeBorrar/puedeDeshacer) — para T5, que exporta este
   *  mismo componente como `ActividadLista` (plan §3 R3: «sin tocar el
   *  diseño»). `Actividad` no lo pasa: default false, mismo comportamiento
   *  de siempre. */
  soloLectura?: boolean;
  onEditar: () => void;
  onBorrar: () => void;
  onDeshacer: () => void;
}

// Fila del hilo: exportado para que T5 lo reuse tal cual como
// `ActividadLista` (plan §3 R3) — con `soloLectura` no muestra ninguna
// acción, sea cual sea puedeEditar/puedeBorrar/puedeDeshacer.
export function ActividadItemRow({
  item,
  rdtId,
  rdtNro,
  migrada,
  puedeEditar,
  puedeBorrar,
  puedeDeshacer,
  step,
  domId,
  highlightPhase,
  soloLectura = false,
  onEditar,
  onBorrar,
  onDeshacer,
}: ActividadItemRowProps) {
  // accionable: el ítem ADMITE acciones por su tipo (independiente de si
  // ESTE usuario puede ejercerlas — eso ya viene resuelto en puedeEditar/
  // puedeBorrar/puedeDeshacer) Y de que el caller no pidió solo lectura.
  const accionable =
    !soloLectura &&
    ((item.t === 'comentario' && !item.borrado) || item.t === 'monto' || item.t === 'referencia' || (item.t === 'hito' && puedeDeshacer));

  return (
    <TimelineItem step={step} id={domId} role="listitem" className={cn('group relative', CLASE_ITEM)}>
      <TimelineIndicator className={CLASE_INDICADOR}>
        {item.t === 'comentario' || item.t === 'creacion' ? (
          <ActividadAvatar nombre={item.autor} fotoUrl={item.fotoUrl} />
        ) : (
          <IconTile
            variant="soft"
            size="xs"
            // 22px con glifo de 12px, paridad con el nodo de la rc.1 (ver la
            // nota de CLASE_INDICADOR más arriba), mismo tamaño en las 3
            // apariciones (grep IconTile).
            className={cn(ICON_TINT[item.t], '[--icon-tile-size:22px] [--icon-tile-icon-size:12px]')}
            aria-hidden="true"
          >
            {(() => {
              // Campanita para los ecos de posponer (encargo G3, hallazgo 7
              // de Juan: «Pospuse la 61 de la 1140 y en Actividad tal vez
              // usaría una campanita») — el resto de 'evento' sigue con
              // ArrowRightLeft (ICONS), sin tocar el mapa por tipo general.
              const Icon = item.t === 'evento' && item.campo === 'posponer' ? BellRing : ICONS[item.t];
              return <Icon strokeWidth={2.2} />;
            })()}
          </IconTile>
        )}
      </TimelineIndicator>
      <TimelineSeparator className={CLASE_SEPARADOR} />

      <TimelineContent
        className={cn(
          'min-w-0 rounded-[10px] px-2.5 py-[9px] text-foreground hover:bg-surface-2',
          // La transición SOLO en 'fading' (review v0.9.0): con la clase también
          // en 'on', el bg-primary/12 arrancaba como fade-IN de 1400ms que el
          // rAF revertía al frame siguiente — el resaltado quedaba invisible.
          // 'on' pinta el bg en el primer paint, sin animar; 'fading' lo retira
          // CON transición y el navegador anima la vuelta a transparente.
          highlightPhase === 'fading' && 'transition-colors duration-[1400ms] ease-out',
          highlightPhase === 'on' && 'bg-primary/12',
        )}
      >
        <div
          className={cn(
            // La reserva de mouse (50px) alcanza para el grupo de acciones a
            // 24px de siempre; en táctil ese mismo grupo (Editar/Borrar/
            // Deshacer, hasta 3 × icon-xs) crece a 44px cada uno por la
            // regla de R0 — 3×44 + 2 gaps de 2px (gap-0.5) ≈ 136px, más el
            // `right-[7px]` del contenedor: sin esto, revelado por teclado/
            // VoiceOver, el grupo tapa la cabecera del ítem (hallazgo MEDIA,
            // lente `obra-comportamiento` de la QA de R3).
            // Condicionada a `!soloLectura` (contrato de `ActividadListaProps`
            // en panel-avivador-tipos.ts; hallazgo ALTA de la verificación de
            // T5, verif.md §6.1): con `soloLectura` (el panel del Avivador,
            // 440px) `accionable` es siempre false, así que no hay nada que
            // reservar — sin condicionar, la reserva fija cortaba texto que
            // entraba bien en la columna ancha de la obra.
            'flex flex-wrap gap-x-[7px] gap-y-1',
            !soloLectura && 'pr-[50px] [@media(hover:none)]:pr-[150px]',
            item.t === 'comentario' || item.t === 'creacion' ? 'items-baseline' : 'items-center',
          )}
        >
          {item.t === 'comentario' || item.t === 'creacion' ? (
            <span className="text-xs font-semibold">{item.autor}</span>
          ) : item.t === 'hito' ? (
            // Hito: a diferencia del resto de los pasos de sistema (rama de
            // abajo), SIEMPRE suma el nombre como texto visible además del
            // avatar — antes ningún hito lo mostraba (ni con autor real, el
            // avatar quedaba solo con el tooltip nativo) y un hito pesa lo
            // mismo que "Rede creada" en el relato del hilo. Fix feedback
            // Juan 2026-08-28 (obra 1130 rede #28): "Facturado" mostraba
            // solo el avatar "H", sin texto — `item.autor` ya resuelve
            // nombre real o AUTOR_HISTORICO (nombreAutorOpcional en
            // rdt-domain.ts), así que alcanza con mostrarlo tal cual, mismo
            // patrón que comentario/creación arriba.
            <>
              <AvatarPersona nombre={item.autor} fotoUrl={item.fotoUrl} size={16} />
              <span className="text-xs font-semibold">{item.autor}</span>
            </>
          ) : (
            // Resto de los pasos de sistema (chequeo/contacto/adjunto/monto/
            // referencia/evento): avatar en vez de nombre en texto — el
            // AvatarPersona ya lleva title=nombre, tooltip nativo cubre la
            // autoría de un autor real (sin cambios acá). Excepción: autor
            // HISTÓRICO (migrada sin fila real) siempre suma el texto
            // "Histórico" visible junto al avatar — "nada de avatar solo"
            // para autoría histórica (decisión Juan 2026-08-28, mismo
            // feedback que el hito de arriba; antes el builder de chequeo
            // ni siquiera pintaba el avatar bien, mostraba "—").
            <>
              <AvatarPersona nombre={item.autor} fotoUrl={item.fotoUrl} size={16} />
              {item.autor === AUTOR_HISTORICO && <span className="text-xs font-semibold">{AUTOR_HISTORICO}</span>}
            </>
          )}
          <span className="text-[10.5px] tabular-nums text-text-tertiary">{fHora(item.t === 'creacion' ? item.fechaAlta : item.ts)}</span>
        </div>

        {item.t === 'comentario' &&
          (item.borrado ? (
            <p className="m-0 mt-0.5 text-[12.5px] italic text-muted-foreground">
              Comentario borrado por {item.borradoPor ?? 'alguien'} · {fHora(item.ts)}
            </p>
          ) : (
            <>
              <p className="m-0 mt-0.5 whitespace-pre-wrap break-words text-[12.5px] leading-[1.45] text-muted-foreground">{item.texto}</p>
              {item.editado && <span className="text-[10px] italic text-muted-foreground">(editado)</span>}
            </>
          ))}

        {item.t === 'creacion' && (
          <p className="m-0 mt-px text-[12.5px] font-[550]">
            Rede #{rdtNro} creada
            {migrada && <span className="ml-1 font-normal italic text-muted-foreground">— migrada del histórico</span>}
          </p>
        )}

        {item.t === 'hito' && (
          <>
            <p className="m-0 mt-px text-[12.5px] font-[550]">
              {item.nombre}
              {/* Fecha de NEGOCIO del hito (puede ser retroactiva) — el hilo
                  ordena por completadoAt (ts, header de arriba), esto solo la
                  muestra, mismo patrón que el ítem contacto (punto 13 v0.9.0). */}
              <span className="font-normal text-muted-foreground"> · {fFecha(item.fecha)}</span>
            </p>
            {item.nota && <p className="m-0 mt-px text-[11.5px] text-muted-foreground">{item.nota}</p>}
            {/* Marca «(editado)» (BACKLOG «Hitos editados sin marca
                (editado)», Juan #15): fecha corregida vía "Corregir hitos"
                — mismo sufijo/estilo que chequeo/monto/referencia. */}
            {item.editado && <span className="text-[10px] italic text-muted-foreground">(editado)</span>}
          </>
        )}

        {item.t === 'chequeo' && (
          <>
            <p className="m-0 mt-px text-[12.5px] font-[550]">
              Chequeo {perLabel(item.periodo)}
              {item.valorPct != null ? ` · ${fmtPct.format(item.valorPct)} %` : ''} —{' '}
              {item.salta ? <span className="text-salta">SALTA</span> : 'no salta'}
            </p>
            <p className="m-0 mt-px text-[11.5px] text-muted-foreground">
              {/* Hallazgo 5, QA ronda 2 (Juan 2026-09-15): mostraba la fecha ISO
                  cruda ("índices del 2023-04-28") — fFecha ya la formatea es-AR,
                  mismo helper que usa el ítem de hito de arriba. */}
              índices del {fFecha(item.fechaIndices) ?? '—'}
              {item.nota ? ` · ${item.nota}` : ''}
            </p>
          </>
        )}

        {item.t === 'monto' && (
          <>
            <p className="m-0 mt-px flex flex-wrap items-center gap-1.5 text-[12.5px] font-[550]">
              <span>
                {/* Monto y % son los dos opcionales (decisión de Juan, QA
                    2026-09-14: Definitivas de SOFSE/ADIF por FRI/%) — se
                    muestran las partes presentes, «sin monto» solo si no
                    hay ninguna de las dos. */}
                Monto informado:{' '}
                {[item.monto != null ? fmtMonto.format(item.monto) : null, item.valorPct != null ? `${fmtPct.format(item.valorPct)} %` : null]
                  .filter((p): p is string => p !== null)
                  .join(' · ') || 'sin monto'}{' '}
                · per. {perLabel(item.periodo)}
              </span>
              {item.vigente && (
                // Vuelve a verde (decisión de Juan 28/9, qa_visual_reui_2026-09-28.md
                // §7.4/U-12): antes salió `variant="secondary"` sin OK — un
                // chip gris sin significado. `success-light` es el tono
                // vendorizado que ya trae su propia variante dark; el
                // uppercase/font-bold/tracking es paridad tipográfica con el
                // chip a mano de la rc.1 (`bg-success/15 text-success
                // uppercase font-bold tracking-[0.04em]`, ver
                // scratchpad/antes-rc1/…/actividad.tsx ~306).
                <Badge variant="success-light" size="sm" radius="full" className="font-bold uppercase tracking-[0.04em]">
                  vigente
                </Badge>
              )}
            </p>
            <p className="m-0 mt-px text-[11.5px] text-muted-foreground">
              {fFecha(item.fecha)}
              {item.nota ? ` · ${item.nota}` : ''}
            </p>
            {item.editado && <span className="text-[10px] italic text-muted-foreground">(editado)</span>}
          </>
        )}

        {item.t === 'referencia' && (
          <>
            <p className="m-0 mt-px flex flex-wrap items-center gap-1.5 font-mono text-[12.5px] font-[550]">
              <span>Expediente: {item.referencia}</span>
              {item.vigente && (
                // Mismo fix que el badge de monto de arriba (U-12) — `font-sans`
                // pisa el `font-mono` que hereda del `<p>` de Expediente.
                <Badge
                  variant="success-light"
                  size="sm"
                  radius="full"
                  className="font-sans font-bold uppercase tracking-[0.04em]"
                >
                  vigente
                </Badge>
              )}
            </p>
            {item.nota && <p className="m-0 mt-px text-[11.5px] text-muted-foreground">{item.nota}</p>}
            {item.editado && <span className="text-[10px] italic text-muted-foreground">(editado)</span>}
          </>
        )}

        {item.t === 'evento' && (
          <>
            <p className="m-0 mt-px text-[12.5px] font-[550]">
              {item.campo === 'archivada' ? (
                item.despues
              ) : item.campo === 'posponer' ? (
                // Eco del Avivador (encargo G3, hallazgo 7): el alta manda
                // `despues` = «hasta dd/mm/aa» (routes/avivador.ts), el
                // cancelar manda literal «cancelada» — se muestra tal cual,
                // sin reformatear una fecha que ya viene en es-AR.
                item.despues === 'cancelada' ? (
                  'Posposición cancelada'
                ) : (
                  `Pospuesta ${item.despues ?? ''}`
                )
              ) : item.campo === 'chequeo' ? (
                // Corrección de chequeo vía PATCH /:id/chequeos/:chid (ecos
                // de corrección, Juan QA 2026-09-14: corrigió el período de
                // un chequeo migrado — feb-22 → feb-23 — y "no se guardó en
                // la actividad"). El backend manda antes/despues como
                // "<etiqueta> · <valor crudo>" (mismo separador « · » que
                // el eco de hito) — acá se separa y formatea es-AR por
                // etiqueta (eventoValor, rdt-domain.ts).
                (() => {
                  // Etiqueta NO codiciosa (`.*?`): desde «dar eco a todo»
                  // (0.12.0) el valor puede ser la nota libre del paso, que
                  // puede contener « · » — con `(.*)` codicioso la etiqueta
                  // se comía todo hasta el último separador.
                  const mA = /^(.*?) · (.*)$/s.exec(item.antes ?? '');
                  const mD = /^(.*?) · (.*)$/s.exec(item.despues ?? '');
                  const etiqueta = mD?.[1] ?? mA?.[1] ?? '';
                  const antesFmt = mA ? eventoValor(mA[1], mA[2]) : '—';
                  const despuesFmt = mD ? eventoValor(mD[1], mD[2]) : '—';
                  return (
                    <>
                      Corrigió el chequeo — {etiqueta}: <span className="font-normal text-muted-foreground">{antesFmt}</span>
                      {' → '}
                      <span className="font-normal text-muted-foreground">{despuesFmt}</span>
                    </>
                  );
                })()
              ) : item.campo === 'monto' ? (
                // Corrección de un paso «Monto informado» vía PATCH
                // /:id/montos/:mid (0.11.0, mismo patrón que chequeo arriba
                // — «toda corrección deja eco en el hilo»): el backend manda
                // antes/despues "<etiqueta> · <valor crudo>" con etiqueta
                // monto/%/período/fecha.
                (() => {
                  // Etiqueta NO codiciosa (`.*?`): desde «dar eco a todo»
                  // (0.12.0) el valor puede ser la nota libre del paso, que
                  // puede contener « · » — con `(.*)` codicioso la etiqueta
                  // se comía todo hasta el último separador.
                  const mA = /^(.*?) · (.*)$/s.exec(item.antes ?? '');
                  const mD = /^(.*?) · (.*)$/s.exec(item.despues ?? '');
                  const etiqueta = mD?.[1] ?? mA?.[1] ?? '';
                  const antesFmt = mA ? eventoValor(mA[1], mA[2]) : '—';
                  const despuesFmt = mD ? eventoValor(mD[1], mD[2]) : '—';
                  return (
                    <>
                      Corrigió el monto informado — {etiqueta}: <span className="font-normal text-muted-foreground">{antesFmt}</span>
                      {' → '}
                      <span className="font-normal text-muted-foreground">{despuesFmt}</span>
                    </>
                  );
                })()
              ) : item.campo === 'referencia' ? (
                // Corrección del paso «Expediente» vía PATCH
                // /:id/referencias/:rid (v0.14.0, Tramo E de la auditoría —
                // era la única corrección de la app sin eco; regla de Juan
                // 14/9 «toda corrección deja eco»). Mismo formato que
                // chequeo/monto: "<etiqueta> · <valor>" con etiqueta
                // expediente/nota.
                (() => {
                  const mA = /^(.*?) · (.*)$/s.exec(item.antes ?? '');
                  const mD = /^(.*?) · (.*)$/s.exec(item.despues ?? '');
                  const etiqueta = mD?.[1] ?? mA?.[1] ?? '';
                  const antesFmt = mA ? eventoValor(mA[1], mA[2]) : '—';
                  const despuesFmt = mD ? eventoValor(mD[1], mD[2]) : '—';
                  return (
                    <>
                      Corrigió el expediente — {etiqueta}: <span className="font-normal text-muted-foreground">{antesFmt}</span>
                      {' → '}
                      <span className="font-normal text-muted-foreground">{despuesFmt}</span>
                    </>
                  );
                })()
              ) : item.campo === 'hito' ? (
                item.despues !== null ? (
                  // Corrección de fecha de un hito vía "Corregir hitos" /
                  // PUT /:id (ecos de corrección, Juan QA 2026-09-14):
                  // antes/despues vienen "Nombre · YYYY-MM-DD" en los DOS
                  // — se distingue del deshacer (despues === null, rama de
                  // abajo, que se sigue mostrando igual que hoy) por eso.
                  (() => {
                    const mA = /^(.*) · (\d{4}-\d{2}-\d{2})$/.exec(item.antes ?? '');
                    const mD = /^(.*) · (\d{4}-\d{2}-\d{2})$/.exec(item.despues!);
                    const nombre = mD?.[1] ?? mA?.[1] ?? '—';
                    return (
                      <>
                        Corrigió la fecha de «{nombre}»:{' '}
                        <span className="font-normal text-muted-foreground">{mA ? fFecha(mA[2]) : '—'}</span>
                        {' → '}
                        <span className="font-normal text-muted-foreground">{mD ? fFecha(mD[2]) : '—'}</span>
                      </>
                    );
                  })()
                ) : (
                  // Deshacer un hito (punto 11a v0.9.0): no es un "a → b", es
                  // una acción sobre un paso puntual del ciclo. El backend
                  // guarda `antes` como "Nombre · YYYY-MM-DD" (convención ISO
                  // cruda de rdt_eventos) — acá se separa y formatea es-AR.
                  (() => {
                    const crudo = item.antes ?? item.despues ?? '—';
                    const m = /^(.*) · (\d{4}-\d{2}-\d{2})$/.exec(crudo);
                    return m ? (
                      <>
                        Deshizo el paso «{m[1]}»
                        <span className="font-normal text-muted-foreground"> — tenía fecha {fFecha(m[2])}</span>
                      </>
                    ) : (
                      <>Deshizo el paso «{crudo}»</>
                    );
                  })()
                )
              ) : item.campo === 'tipo' ? (
                // Cambio Provisoria↔Definitiva (punto 3 v0.9.0).
                <>
                  Cambió el tipo: <span className="font-normal text-muted-foreground">{item.antes ?? '—'}</span>
                  {' → '}
                  <span className="font-normal text-muted-foreground">{item.despues ?? '—'}</span>
                </>
              ) : (
                <>
                  {eventoCampoLabel(item.campo)} modificado: <span className="font-normal text-muted-foreground">{item.antes ?? '—'}</span>
                  {' → '}
                  <span className="font-normal text-muted-foreground">{item.despues ?? '—'}</span>
                </>
              )}
            </p>
            {item.nota && <p className="m-0 mt-px text-[11.5px] text-muted-foreground">{item.nota}</p>}
          </>
        )}

        {item.t === 'contacto' && (
          <>
            <p className="m-0 mt-px text-[12.5px] font-[550]">
              Contacto con {interlocutorLabel(item.interlocutor)} — {item.resultado === 'sin_respuesta' ? 'sin respuesta' : 'con novedad'}
              {/* Fecha REAL del contacto (puede ser retroactiva) — el hilo
                  sigue ordenado por creadoAt (ts), esto solo la muestra. */}
              <span className="font-normal text-muted-foreground"> · {fFecha(item.fecha)}</span>
            </p>
            {item.nota && <p className="m-0 mt-px text-[11.5px] text-muted-foreground">{item.nota}</p>}
          </>
        )}

        {item.t === 'adjunto' && (
          <>
            <p className="m-0 mt-px text-[12.5px] font-[550]">{tipoAdjuntoLabel(item.tipo)}</p>
            <Button variant="outline" size="sm" render={<a href={adjuntoHref(rdtId, item.id)} />} nativeButton={false} className="mt-1">
              <Paperclip aria-hidden="true" />
              {item.nombre} <span className="text-[10.5px] text-text-tertiary">{tamanoLegible(item.tamanoBytes)}</span>
            </Button>
            {item.nota && <p className="m-0 mt-1 text-[11.5px] text-muted-foreground">{item.nota}</p>}
          </>
        )}
      </TimelineContent>

      {accionable && (
        <div className="absolute right-[7px] top-1.5 flex gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
          {/* Paridad con la rc.1 (rc.6, qa_visual_reui_2026-09-28.md, revisión
              fuera de tabla): Borrar lleva el tinte rojo (`hover:bg-
              destructive/10 hover:text-destructive`), la única señal de que
              es la acción destructiva del grupo — Editar/Deshacer comparten
              el gris neutro de la variante ghost (`hover:bg-plate
              hover:text-foreground`). El guard completo `not-aria-disabled:
              hover:*` (no `hover:*` a secas) es necesario para ganar por
              especificidad: es el mismo classGroup de color que el hover por
              defecto de `ghost` en ui/button.tsx (`not-aria-disabled:hover:
              bg-muted not-aria-disabled:hover:text-foreground`), así que sin
              igualar la cadena completa de modificadores twMerge no ve
              conflicto y el navegador prioriza el selector con más
              especificidad (`:not([aria-disabled])`). */}
          {puedeEditar && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Editar"
                    onClick={onEditar}
                    className="text-text-tertiary not-aria-disabled:hover:bg-plate not-aria-disabled:hover:text-foreground"
                  />
                }
              >
                <Pencil aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent>Editar</TooltipContent>
            </Tooltip>
          )}
          {puedeBorrar && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Borrar"
                    onClick={onBorrar}
                    className="text-text-tertiary not-aria-disabled:hover:bg-destructive/10 not-aria-disabled:hover:text-destructive"
                  />
                }
              >
                <Trash2 aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent>Borrar</TooltipContent>
            </Tooltip>
          )}
          {puedeDeshacer && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Deshacer"
                    onClick={onDeshacer}
                    className="text-text-tertiary not-aria-disabled:hover:bg-plate not-aria-disabled:hover:text-foreground"
                  />
                }
              >
                <Undo2 aria-hidden="true" />
              </TooltipTrigger>
              <TooltipContent>Deshacer</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </TimelineItem>
  );
}

// `ActividadLista` (T5-A, plan_build_avivador_v2.md «T5 — Actividad, Comentar,
// cambio de versión y limpieza», A1; firma documentada y consumida por T5-B
// en components/avivador/panel-avivador-tipos.ts): el hilo completo de una
// rede para el panel del Avivador — mismo `actividadItems(rdt)` (ya ordenado
// desc por fecha, más nueva primero) y mismo `ActividadItemRow` que la
// columna de la obra más abajo, sin tocar el diseño.
// - `puedeEditar`/`puedeBorrar`/`puedeDeshacer` van siempre en `false`: este
//   componente no recibe `currentUserId` ni `esAdmin` (`ActividadListaProps`
//   solo trae `rdt` y `soloLectura`), así que no hay con qué resolver
//   autoría real — y de todos modos `soloLectura` ya oculta cualquier acción
//   (el `accionable` de `ActividadItemRow`, arriba) sea cual sea su valor.
// - `highlightPhase` siempre `null`: acá no hay lápida ni stepper que
//   dispare el resaltado one-shot de la obra.
// - `domId` con el prefijo `avv-act-` (la obra usa `act-`), para que los ids
//   nunca choquen si algún día las dos vistas coexisten en el DOM.
// - Sin contenedor propio de scroll (a diferencia de la columna de la obra):
//   scrollea el panel entero que la monta (§13 7.1, «scroll infinito» = se
//   carga la historia completa al abrir, sin paginar).
// `Actividad` (la de la obra, más abajo) no cambia.
export function ActividadLista({ rdt, soloLectura }: ActividadListaProps) {
  const items = actividadItems(rdt);

  return (
    <Timeline role="list" aria-label="Historial de actividad">
      {items.map((item, index) => (
        <ActividadItemRow
          key={`${itemKey(item)}-${item.ts}`}
          item={item}
          rdtId={rdt.id}
          rdtNro={rdt.nro}
          migrada={rdt.migrada}
          puedeEditar={false}
          puedeBorrar={false}
          puedeDeshacer={false}
          step={index + 1}
          domId={`avv-act-${itemKey(item)}`}
          highlightPhase={null}
          soloLectura={soloLectura}
          onEditar={() => {}}
          onBorrar={() => {}}
          onDeshacer={() => {}}
        />
      ))}
    </Timeline>
  );
}

// Mini-form in place para corregir un paso "Monto informado" ya cargado
// (nunca cargar otro paso con fecha vieja para arreglar un valor — resolución
// #3 del panel: corregir = editar ESE paso, con marca "editado").
function MontoEditForm({
  obra,
  rdtId,
  item,
  onDone,
}: {
  obra: ObraDetalle;
  rdtId: number;
  item: Extract<ActividadItem, { t: 'monto' }>;
  onDone: () => void;
}) {
  const editarMonto = useEditarMonto();
  const [monto, setMonto] = useState<number | null>(item.monto);
  const [pct, setPct] = useState<number | null>(item.valorPct);
  const [periodo, setPeriodo] = useState(item.periodo);
  const [fecha, setFecha] = useState(item.fecha.slice(0, 10));
  // Nota (Juan, validación v0.12.0, 2026-09-15: «la nota es del acto, no del
  // dato», mismo criterio que EditarChequeoForm en chequeos-tabla.tsx): un
  // solo campo de texto por acto sobre el paso, SIEMPRE vacío — antes
  // coexistían "Nota del paso" (precargada, editaba item.nota) y "Motivo de
  // la corrección" (vacía, viajaba como motivo del eco); ahora hay un único
  // campo que siempre viaja como motivo del eco (rdt_eventos.nota vía
  // `motivo`) y nunca pisa `item.nota` — la nota original del paso ya no se
  // edita desde acá, sigue mostrándose tal cual en el ítem del hilo.
  const [nota, setNota] = useState('');
  const [errorMonto, setErrorMonto] = useState(false);
  // Mismo rango que FormMonto (paso-forms.tsx) UNION el período ya cargado
  // en el ítem, por si cae afuera (redes viejas/migradas) — el valor
  // vigente tiene que aparecer seleccionado y seleccionable siempre (Juan +
  // panel, 2026-09-15: el período pasa a editarse con el mismo <Select> que
  // lo crea, no con un <input type="month"> aparte — Nielsen #4).
  const opciones = useMemo(() => {
    const base = ultimosPeriodos(periodoActual(), 18);
    return base.includes(item.periodo) ? base : [...base, item.periodo].sort();
  }, [item.periodo]);

  // Dirty check calculado en render (QA ronda 2): antes solo vivía dentro de
  // handleGuardar — se saca acá para poder deshabilitar Guardar mientras no
  // hay cambios reales (mismo motivo que el guard `!hayCambios` de abajo,
  // ahora también visible antes de apretar el botón). La nota NUNCA cuenta
  // acá: no es un dato del paso, así que por sí sola no habilita Guardar.
  const hayCambios = monto !== item.monto || pct !== item.valorPct || periodo !== item.periodo || fecha !== item.fecha.slice(0, 10);

  function handleGuardar(): void {
    // Monto Y % son los dos opcionales, al menos uno (decisión de Juan, QA
    // 2026-09-14 — Definitivas de SOFSE/ADIF por FRI/%, mismo relajo que el
    // 422 del backend): si la gestora borra los dos campos y el paso YA
    // tenía algún dato, es un error de carga — se avisa inline y se corta el
    // guardado. Si el paso YA era null/null (migrada J2 sin ninguno de los
    // dos en el legacy, de antes de esta versión) se deja pasar igual —
    // corrige período/fecha sin forzar un dato que no existía.
    const teniaAlgunDato = item.monto !== null || item.valorPct !== null;
    if (teniaAlgunDato && monto === null && pct === null) {
      setErrorMonto(true);
      return;
    }
    setErrorMonto(false);

    const payload: EditarMontoInput = { cc: obra.cc, id: rdtId, mid: item.id };
    let hayCambiosPayload = false;
    if (monto !== item.monto) {
      payload.monto = monto;
      hayCambiosPayload = true;
    }
    if (pct !== item.valorPct) {
      payload.valorPct = pct;
      hayCambiosPayload = true;
    }
    if (periodo !== item.periodo) {
      payload.periodo = periodo;
      hayCambiosPayload = true;
    }
    if (fecha !== item.fecha.slice(0, 10)) {
      payload.fecha = fecha;
      hayCambiosPayload = true;
    }
    // Nota (Juan, 2026-09-15): solo viaja si se escribió algo, siempre como
    // `motivo` — nunca se manda `motivo: null` ni cuenta para el guard de
    // "sin cambios" de acá abajo (no es un dato del paso).
    const notaTrim = nota.trim();
    if (notaTrim) payload.motivo = notaTrim;

    if (!hayCambiosPayload) {
      onDone();
      return;
    }

    editarMonto.mutate(payload, {
      onSuccess: () => {
        toast.success('Monto actualizado');
        onDone();
      },
      onError: err => toast.error(errorMessage(err, 'No se pudo editar el monto')),
    });
  }

  return (
    <div className="min-w-0 flex-1 rounded-[10px] border border-primary/40 bg-surface-card p-2.5">
      {/* auto-fit en vez de grid-cols-2 fijo (Juan + panel, 2026-09-15: la
          captura mostraba «$ 1.000.000,0» y «agosto de :» cortados en la
          columna angosta de Actividad) — mismo patrón que ficha.tsx L439
          para grids con montos, con 160px de mínimo: alcanza para «$ 999.999.999,99» (tope
          del DECIMAL(15,2)) y la fila cae a 1 columna cuando no entran dos. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2">
        <CampoDecimal
          adorno="$"
          valorInicial={item.monto}
          onValueChange={v => {
            setMonto(v);
            setErrorMonto(false);
          }}
        />
        <CampoDecimal
          adorno="%"
          valorInicial={item.valorPct}
          onValueChange={v => {
            setPct(v);
            setErrorMonto(false);
          }}
        />
      </div>
      {errorMonto && <FieldError className="mt-1 text-[10.5px]">El paso tiene que conservar el monto o el porcentaje</FieldError>}
      <div className="mt-2 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2">
        <Select value={periodo} onValueChange={v => v && setPeriodo(v)}>
          <SelectTrigger aria-label="Período" className="w-full">
            <SelectValue>{(v: string) => (v ? perLabel(v) : '—')}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {opciones.map(p => (
              <SelectItem key={p} value={p}>
                {perLabel(p)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div>
          <Input type="date" value={fecha} onChange={e => setFecha(e.target.value)} aria-label="Fecha del cálculo" />
          {esFechaFutura(fecha) && <FieldDescription className="mt-1 text-[10.5px] text-warning">La fecha es futura</FieldDescription>}
        </div>
      </div>
      {/* Nota (Juan, validación v0.12.0, 2026-09-15: «la nota es del acto, no
          del dato»): único campo de texto del form, label chico mismo estilo
          que el popover de chequeos (chequeos-tabla.tsx) — siempre vacío,
          viaja como `motivo` del eco y nunca pisa `item.nota`. La nota
          original del paso ya no se edita desde acá, sigue mostrándose tal
          cual en el ítem del hilo. */}
      <Field className="mt-2">
        <FieldLabel htmlFor={`me-nota-${item.id}`} className="mb-[3px] block text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
          Nota
        </FieldLabel>
        <Input
          id={`me-nota-${item.id}`}
          value={nota}
          onChange={e => setNota(e.target.value)}
          placeholder="Opcional — queda en el hilo con esta corrección"
        />
      </Field>
      <div className="mt-2 flex justify-end gap-1.5">
        <Button type="button" variant="outline" size="sm" onClick={onDone} className="px-2.5 text-[11.5px]">
          Cancelar
        </Button>
        <Button type="button" size="sm" onClick={handleGuardar} disabled={editarMonto.isPending || !hayCambios} className="px-2.5 text-[11.5px]">
          {editarMonto.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
}

// Idem para "Expediente".
function ReferenciaEditForm({
  obra,
  rdtId,
  item,
  onDone,
}: {
  obra: ObraDetalle;
  rdtId: number;
  item: Extract<ActividadItem, { t: 'referencia' }>;
  onDone: () => void;
}) {
  const editarReferencia = useEditarReferencia();
  const [referencia, setReferencia] = useState(item.referencia);
  // Nota del ACTO (v0.14.0, Tramo E de la auditoría — alinea este form con
  // MontoEditForm/EditarChequeoForm, «la nota es del acto, no del dato»,
  // Juan 2026-09-15): siempre vacía, viaja como `motivo` del eco
  // (rdt_eventos.nota) y nunca pisa `item.nota`. Antes este campo editaba la
  // nota del paso — la nota original sigue mostrándose tal cual en el ítem.
  const [nota, setNota] = useState('');
  const hayCambios = referencia.trim() !== item.referencia;

  function handleGuardar(): void {
    const trimmed = referencia.trim();
    if (!trimmed) return;

    const payload: EditarReferenciaInput = { cc: obra.cc, id: rdtId, rid: item.id };
    if (!hayCambios) {
      onDone();
      return;
    }
    payload.referencia = trimmed;
    // Solo viaja si se escribió algo, nunca `motivo: null`; no cuenta para
    // el guard de «sin cambios» (no es un dato del paso).
    const notaTrim = nota.trim();
    if (notaTrim) payload.motivo = notaTrim;

    editarReferencia.mutate(payload, {
      onSuccess: () => {
        toast.success('Expediente actualizado');
        onDone();
      },
      onError: err => toast.error(errorMessage(err, 'No se pudo editar el expediente')),
    });
  }

  return (
    <div className="min-w-0 flex-1 rounded-[10px] border border-primary/40 bg-surface-card p-2.5">
      {/* Apilado a ancho completo, Nota abajo (punto 17 v0.9.0) — espejo del
          FormExpediente de alta (paso-forms.tsx): antes iba en grid-cols-2 y
          la referencia (a veces larga, font-mono) quedaba apretada. */}
      <Input
        value={referencia}
        onChange={e => setReferencia(e.target.value)}
        maxLength={255}
        aria-label="Referencia"
        className="w-full font-mono"
      />
      {/* Nota del acto (v0.14.0): mismo label chico que MontoEditForm — viaja
          como `motivo` del eco, no edita la nota del paso. */}
      <Field className="mt-2">
        <FieldLabel htmlFor={`re-nota-${item.id}`} className="mb-[3px] block text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
          Nota
        </FieldLabel>
        <Input
          id={`re-nota-${item.id}`}
          value={nota}
          onChange={e => setNota(e.target.value)}
          placeholder="Opcional — queda en el hilo con esta corrección"
        />
      </Field>
      <div className="mt-2 flex justify-end gap-1.5">
        <Button type="button" variant="outline" size="sm" onClick={onDone} className="px-2.5 text-[11.5px]">
          Cancelar
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleGuardar}
          disabled={editarReferencia.isPending || !referencia.trim() || !hayCambios}
          className="px-2.5 text-[11.5px]"
        >
          {editarReferencia.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
}

/** Deshacer (punto 11a v0.9.0): confirmación con nota opcional sobre el
 *  ÚLTIMO hito completado — no reusa ConfirmDialog (que no admite un campo
 *  de nota) sino AlertDialog directamente (c-alert-dialog-5), mismo criterio
 *  visual. Los 422 del backend (ej. "hay un hito posterior") se muestran
 *  tal cual en el toast, patrón existente. */
function DeshacerHitoDialog({
  cc,
  rdtId,
  target,
  onClose,
}: {
  cc: string;
  rdtId: number;
  target: { codigo: string; nombre: string } | null;
  onClose: () => void;
}) {
  const deshacerHito = useDeshacerHito();
  const [nota, setNota] = useState('');

  useEffect(() => {
    setNota('');
  }, [target]);

  function handleConfirmar(): void {
    if (!target) return;
    deshacerHito.mutate(
      { cc, id: rdtId, codigo: target.codigo, nota: nota.trim() || null },
      {
        onSuccess: () => {
          toast.success(`Se deshizo «${target.nombre}»`);
          onClose();
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo deshacer el paso')),
      },
    );
  }

  return (
    <AlertDialog open={target !== null} onOpenChange={open => !open && onClose()}>
      <AlertDialogContent size="sm">
        <AlertDialogHeader>
          <AlertDialogTitle>Deshacer paso</AlertDialogTitle>
          <AlertDialogDescription>
            ¿Deshacer «{target?.nombre}»? Es el último hito completado del ciclo — se puede volver a completar después.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Field>
          <FieldLabel htmlFor="deshacer-hito-nota" className="sr-only">
            Nota
          </FieldLabel>
          <Input id="deshacer-hito-nota" value={nota} onChange={e => setNota(e.target.value)} placeholder="Nota (opcional)" autoFocus />
        </Field>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deshacerHito.isPending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction type="button" variant="destructive" onClick={handleConfirmar} disabled={deshacerHito.isPending}>
            {deshacerHito.isPending ? 'Deshaciendo…' : 'Deshacer'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface Props {
  obra: ObraDetalle;
  rdt: RdtDetalle;
  currentUserId: number | null;
  esAdmin: boolean;
  pasoAbierto: { tipo: Exclude<TipoPaso, 'comentario'>; hitoKey?: string } | null;
  onAbrirPaso: (tipo: Exclude<TipoPaso, 'comentario'>, hitoKey?: string) => void;
  onCerrarPaso: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
  /** Scroll+resaltado desde la lápida/stepper (puntos 8/9/14 v0.9.0) — el
   *  `nonce` permite re-disparar sobre el mismo ítem dos veces seguidas. */
  resaltado: { clave: string; nonce: number } | null;
}

// Breakpoint del drawer de Actividad (mismo corte que obra-detalle.tsx:
// max-width 1279px) — reactivo, no un chequeo de una sola vez: hace falta
// saber en cada render si corresponde el Sheet modal (<1280px) o la columna
// fija de siempre, para que el layout no se trabe al redimensionar la
// ventana o al cruzar el corte con el form de «+ Paso» abierto.
function useDrawerActividad(): boolean {
  const [esDrawer, setEsDrawer] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1280);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1279px)');
    const onChange = () => setEsDrawer(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return esDrawer;
}

// Columna derecha: hilo único de Actividad (timeline con línea conectora)
// mezclando montos, referencias, chequeos, hitos completados, ecos de
// identidad, contactos, adjuntos, la creación y los comentarios, desc por
// fecha — + el buscador de pasos "+ Paso". «Comentario» (Juan, validación
// v0.12.0/2026-09-15: «todo es por actividad») es una opción más de esa
// paleta, ya no un composer flotante siempre visible arriba del hilo — abre
// el mismo PasoForm que el resto de los pasos, en el mismo slot.
export function Actividad({
  obra,
  rdt,
  currentUserId,
  esAdmin,
  pasoAbierto,
  onAbrirPaso,
  onCerrarPaso,
  mobileOpen,
  onMobileClose,
  resaltado,
}: Props) {
  const esDrawerLive = useDrawerActividad();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const paletteTriggerRef = useRef<HTMLButtonElement>(null);
  // «+ Paso» solo para gestor/admin (viewers leen el hilo pero no tienen los
  // endpoints de escritura habilitados — ocultar, no deshabilitar; mismo
  // criterio que la botonera de la ficha). Con la RDT archivada el backend
  // rechaza TODA escritura con 422 (Cierres finales, 2026-07-11) — se
  // pliega acá mismo para que «+ Paso», el PasoForm (Comentario incluido) y
  // el empty state invitacional queden ocultos/estáticos sin repetir
  // `&& !rdt.archivada` en cada sitio que ya usa `puedeEscribir`.
  const { data: me } = useMe();
  const puedeEscribir = (me?.rol === 'gestor' || me?.rol === 'admin') && !rdt.archivada;
  // FIX permisos (review adversarial 2026-07-10): el backend solo permite
  // editar/borrar un paso de monto o referencia a su AUTOR, a un admin, o a
  // cualquier gestor si el paso es histórico (autor null, migradas J2) —
  // mostrarle el lápiz/borrar a cualquier gestor generaba un 403 sorpresivo.
  function puedeEditarPaso(autorId: number | null): boolean {
    if (!me) return false;
    return autorId === null || autorId === me.id || me.rol === 'admin';
  }
  // «Comentario» abierto vía «+ Paso» (Juan, validación v0.12.0/2026-09-15):
  // estado LOCAL de este componente porque el `pasoAbierto` que dueña
  // obra-detalle.tsx lo excluye a propósito (`PasoAbierto` ahí es
  // `Exclude<TipoPaso, 'comentario'>`) — el mismo PasoForm se monta igual,
  // solo que este tipo puntual no pasa por el prop del padre.
  const [comentarioAbierto, setComentarioAbierto] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [editTexto, setEditTexto] = useState('');
  const [editandoMontoId, setEditandoMontoId] = useState<number | null>(null);
  const [editandoReferenciaId, setEditandoReferenciaId] = useState<number | null>(null);
  // Modo de layout CONGELADO mientras haya un «+ Paso» o una edición inline
  // abiertos (hallazgo ALTA, R3-QA: cruzar 1280px remonta <Sheet> ↔
  // <section> — tipos de elemento distintos en la misma posición del árbol
  // — y React descarta el subárbol completo, incluido cualquier PasoForm
  // montado adentro con su propio useState, borrando en silencio lo
  // tipeado). Acá adentro se sabe qué hay abierto por las mismas props/
  // estado que ya arman el form (pasoAbierto del padre + los locales de
  // edición inline); mientras `algoAbierto` sea true, `esDrawer` se queda
  // en el último modo resuelto — recién se actualiza al valor real de
  // useDrawerActividad() cuando no hay nada abierto.
  const algoAbierto =
    pasoAbierto !== null || comentarioAbierto || editandoId !== null || editandoMontoId !== null || editandoReferenciaId !== null;
  const esDrawerRef = useRef(esDrawerLive);
  if (!algoAbierto) esDrawerRef.current = esDrawerLive;
  const esDrawer = esDrawerRef.current;
  const [confirmState, setConfirmState] = useState<ConfirmDialogState | null>(null);
  // Target del diálogo "Deshacer" (punto 11a v0.9.0) — null = cerrado.
  const [deshacerTarget, setDeshacerTarget] = useState<{ codigo: string; nombre: string } | null>(null);
  // Resaltado one-shot del scroll desde la lápida/stepper (puntos 8/9/14
  // v0.9.0) — `clave` es la del ítem, `phase` las dos etapas de la
  // animación CSS por clases (ver ActividadItemRow: 'on' aplica el bg sin
  // transición en el primer paint, 'fading' lo retira CON transición para
  // que el navegador anime el fade de vuelta a transparente).
  const [highlight, setHighlight] = useState<{ clave: string; phase: 'on' | 'fading' } | null>(null);

  const actualizarComentario = useActualizarComentario();
  const borrarComentario = useBorrarComentario();
  const borrarMonto = useBorrarMonto();
  const borrarReferencia = useBorrarReferencia();

  // El paso «Comentario» (y cualquier edición en curso) es estado LOCAL de
  // este componente, que useRdt cachea por id (staleTime 15s) — al volver a
  // una RDT ya visitada isPending pasa a false de inmediato y
  // obra-detalle.tsx NO remonta Actividad, así que sin este reset el
  // borrador sobrevive al cambio de selección y se publica contra la RDT
  // equivocada.
  useEffect(() => {
    setComentarioAbierto(false);
    setEditandoId(null);
    setEditTexto('');
    setEditandoMontoId(null);
    setEditandoReferenciaId(null);
    setPaletteOpen(false);
    setDeshacerTarget(null);
  }, [rdt.id]);

  // Guarda de "un solo form abierto" entre pasoAbierto (dueño
  // obra-detalle.tsx) y comentarioAbierto (local acá): la Ficha y el
  // CicloVida (hermanos de Actividad) también llaman a onAbrirPaso
  // directo, sin pasar por handlePaletteSelect de acá abajo — sin este
  // efecto, abrir un paso desde ESOS lugares mientras el form de
  // Comentario está abierto dejaría comentarioAbierto en `true` sin
  // apagarlo, y reaparecería solo al cerrar el paso nuevo. El `!pasoAbierto`
  // del render de más abajo ya evita el parpadeo de los dos juntos en el
  // mismo frame; esto sincroniza el estado para los cierres siguientes.
  useEffect(() => {
    if (pasoAbierto) setComentarioAbierto(false);
  }, [pasoAbierto]);

  const items = actividadItems(rdt);
  // Último hito completado del catálogo — el ÚNICO ítem 'hito' con affordance
  // de "Deshacer" (punto 11a v0.9.0: de a uno, orden inverso estricto).
  const ultimoHito = ultimoHitoCompletado(rdt.hitos);

  // Scroll+resaltado hacia un ítem del hilo (puntos 8/9/14 v0.9.0): dispara
  // en cada cambio de `resaltado` (el `nonce` de obra-detalle.tsx garantiza
  // que cambie incluso repitiendo la misma `clave`, para poder re-disparar
  // sobre el mismo ítem dos veces seguidas).
  useEffect(() => {
    if (!resaltado) return;
    const el = document.getElementById(`act-${resaltado.clave}`);
    if (!el) {
      // Defensivo: el caller (ciclo-vida.tsx) ya avisa con su propio toast
      // cuando sabe de antemano que un step "histórico" no tiene rastro en
      // el hilo (punto 9) — esto cubre cualquier otro desajuste de ancla.
      toast('No se encontró ese paso en el hilo.');
      return;
    }
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'center' });

    setHighlight({ clave: resaltado.clave, phase: 'on' });
    // DOBLE rAF (review v0.9.0): un rAF simple dispara ANTES del próximo
    // paint — la fase 'on' nunca llegaba a pintarse y el navegador coalescía
    // los dos cambios de estilo en uno (sin estado inicial pintado no hay
    // transición: el resaltado era invisible). El primer rAF corre antes del
    // paint que pinta 'on'; el segundo recién en el frame siguiente, con el
    // bg ya en pantalla como punto de partida del fade.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setHighlight({ clave: resaltado.clave, phase: 'fading' }));
    });
    const timeout = setTimeout(() => setHighlight(null), 1600);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timeout);
    };
  }, [resaltado]);

  function handlePaletteSelect(tipo: TipoPaso): void {
    setPaletteOpen(false);
    if (tipo === 'comentario') {
      // «Comentario» es un paso más (Juan, validación v0.12.0/2026-09-15):
      // abre el mismo slot de PasoForm que el resto — si había otro paso
      // abierto (pasoAbierto, dueño en obra-detalle.tsx), lo reemplaza, sin
      // diálogo de confirmación (mismo criterio que onAbrirPaso más abajo).
      onCerrarPaso();
      setComentarioAbierto(true);
      return;
    }
    setComentarioAbierto(false);
    if (tipo === 'hito') {
      const next = proximoPaso(lifecycle(rdt));
      if (!next) {
        toast('El ciclo ya está completo');
        return;
      }
      if (next === 'salto') {
        onAbrirPaso('chequeo');
        return;
      }
      // Espejo de Definitivas: el primer paso del ciclo ("Calculado") abre
      // el form de Monto informado, no el genérico de hitos.
      if (next === 'calculado') {
        onAbrirPaso('monto');
        return;
      }
    }
    onAbrirPaso(tipo);
  }

  function handleGuardarEdicion(): void {
    if (editandoId === null) return;
    const trimmed = editTexto.trim();
    if (!trimmed) return;
    actualizarComentario.mutate(
      { cc: obra.cc, id: rdt.id, cid: editandoId, texto: trimmed },
      {
        onSuccess: () => setEditandoId(null),
        onError: err => toast.error(errorMessage(err, 'No se pudo editar el comentario')),
      },
    );
  }

  function pedirBorrarComentario(item: Extract<ActividadItem, { t: 'comentario' }>): void {
    setConfirmState({
      title: 'Borrar comentario',
      description: `¿Borrar el comentario de ${item.autor} del ${fHora(item.ts)}?`,
      confirmLabel: 'Borrar comentario',
      destructive: true,
      onConfirm: () => {
        borrarComentario.mutate(
          { cc: obra.cc, id: rdt.id, cid: item.id },
          { onError: err => toast.error(errorMessage(err, 'No se pudo borrar el comentario')) },
        );
      },
    });
  }

  function pedirBorrarMonto(item: Extract<ActividadItem, { t: 'monto' }>): void {
    setConfirmState({
      title: 'Borrar monto informado',
      description: `¿Borrar el monto informado del ${fHora(item.ts)}?${
        item.vigente ? ' Es el vigente — la lápida recae al monto anterior.' : ''
      }`,
      confirmLabel: 'Borrar monto',
      destructive: true,
      onConfirm: () => {
        borrarMonto.mutate(
          { cc: obra.cc, id: rdt.id, mid: item.id },
          { onError: err => toast.error(errorMessage(err, 'No se pudo borrar el monto')) },
        );
      },
    });
  }

  function pedirBorrarReferencia(item: Extract<ActividadItem, { t: 'referencia' }>): void {
    setConfirmState({
      title: 'Borrar expediente',
      description: `¿Borrar la referencia «${item.referencia}»?${item.vigente ? ' Es la vigente — la lápida recae a la anterior.' : ''}`,
      confirmLabel: 'Borrar expediente',
      destructive: true,
      onConfirm: () => {
        borrarReferencia.mutate(
          { cc: obra.cc, id: rdt.id, rid: item.id },
          { onError: err => toast.error(errorMessage(err, 'No se pudo borrar el expediente')) },
        );
      },
    });
  }

  // Contenido compartido por las dos formas de la columna: el Sheet modal
  // (<1280px, Ajuste 29/R3) y la columna fija de siempre (≥1280px) — mismo
  // JSX en los dos casos, pero <Sheet> y <section> (más abajo) son tipos de
  // elemento DISTINTOS en la misma posición del árbol, así que cruzar el
  // corte remontaría este subárbol entero (con cualquier «+ Paso»/edición
  // inline abierto adentro, y lo que se tipeó ahí) si no fuera porque
  // `esDrawer` (arriba) se congela mientras algo esté abierto — no cambia
  // de por sí, es la congelación la que evita el remount.
  const contenido = (
    <>
      {/* pr-12 solo en modo drawer: reserva el lugar de la X de SheetClose
          (absolute top-4 right-4, 44px en táctil, ui/sheet.tsx) — sin esto,
          a 820px «+ Paso» queda debajo del hit-area de cerrar y tocarlo
          cierra el drawer entero (hallazgo ALTA del verificador de R3). */}
      <div className={cn('flex flex-none items-center gap-[7px] px-4 pb-2 pt-[15px]', esDrawer && 'pr-12')}>
        <h3 className="m-0 text-[13px] font-[650]">Actividad</h3>
        {/* Paridad con la rc.1 (rc.6, qa_visual_reui_2026-09-28.md, revisión de
            cabecera fuera de la tabla de hallazgos): el contador era una
            pastilla chica a mano (`bg-surface-2 text-muted-foreground
            text-[11px]`, alto auto por el padding) — el `size="default"` de
            Badge la infló a 20px con `bg-secondary`/`text-xs`, un tono y
            tamaño que no es el aprobado. `h-auto`+`min-w-0` pisan el alto/
            ancho mínimo fijos del tamaño por defecto. */}
        <Badge
          variant="secondary"
          radius="full"
          className="h-auto min-w-0 gap-0 bg-surface-2 px-[7px] py-px text-[11px] font-semibold tabular-nums text-muted-foreground"
        >
          {items.length}
        </Badge>
        {puedeEscribir && (
          <Popover open={paletteOpen} onOpenChange={setPaletteOpen}>
            {/* «+ Paso» a paridad con la rc.1 (píldora chica: rounded-full,
                py-[5px], text-xs, font-[650]) — el `size="sm"` de Button trae
                32px de alto y `rounded-[10px]`, más alto/cuadrado que la
                píldora original; se pisa con className, Button/Popover
                siguen siendo ReUI (props/aria intactos). */}
            <PopoverTrigger
              render={
                <Button
                  ref={paletteTriggerRef}
                  type="button"
                  size="sm"
                  className="ml-auto h-auto gap-[5px] rounded-full px-[11px] py-[5px] text-xs font-[650]"
                />
              }
            >
              <Plus className="h-[13px] w-[13px]" strokeWidth={2.4} />
              Paso
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[264px] overflow-hidden p-0"
              onKeyDown={e => {
                if (e.key !== 'Escape') return;
                // Ajuste 29 (contrato del Esc en la obra): el Esc del
                // «+ Paso» no tiene que burbujear al Esc global de
                // obra-detalle.tsx, que si no cerraría TAMBIÉN lo que esté
                // abierto ahí — se cierra acá mismo y se devuelve el foco
                // al botón, sin dejar que el evento siga de largo.
                e.preventDefault();
                e.stopPropagation();
                setPaletteOpen(false);
                paletteTriggerRef.current?.focus();
              }}
            >
              <PasoPalette
                onSelect={handlePaletteSelect}
                tipo={rdt.tipo}
                yaSalto={rdt.tipo === 'Provisoria' && Boolean(rdt.salto)}
                finalizada={rdt.finalizada}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Slot único de paso abierto (Juan, validación v0.12.0/2026-09-15:
          «todo es por actividad») — pasoAbierto (dueño obra-detalle.tsx,
          excluye 'comentario' a propósito) y comentarioAbierto (local,
          acá abajo) son mutuamente excluyentes. El `!pasoAbierto` de la
          segunda condición no es solo estético: si Comentario está
          abierto y la Ficha/CicloVida disparan un paso NUEVO directo (sin
          pasar por handlePaletteSelect, ver el efecto de arriba), este
          `!pasoAbierto` evita mostrar los dos a la vez en ESE frame,
          mientras el efecto sincroniza comentarioAbierto para los cierres
          siguientes. */}
      {puedeEscribir && pasoAbierto && <PasoForm tipo={pasoAbierto.tipo} hitoKey={pasoAbierto.hitoKey} obra={obra} rdt={rdt} onClose={onCerrarPaso} />}
      {puedeEscribir && !pasoAbierto && comentarioAbierto && (
        <PasoForm
          tipo="comentario"
          obra={obra}
          rdt={rdt}
          onClose={() => {
            setComentarioAbierto(false);
            // Al desmontar el textarea enfocado el foco caería a <body>
            // (review adversarial v0.13.0): vuelve a «+ Paso», que es de
            // donde salió. El resto de los pasos no lo hace todavía
            // (BACKLOG, a11y).
            paletteTriggerRef.current?.focus();
          }}
        />
      )}

      {items.length === 0 ? (
        // Paridad con la rc.1 (rc.6, qa_visual_reui_2026-09-28.md, revisión
        // fuera de tabla): era UNA sola línea (`text-xs text-text-tertiary`,
        // sin título separado). Partirla en EmptyTitle/EmptyDescription metía
        // dos tipografías distintas (título con `font-heading tracking-tight`
        // del vendorizado, descripción en `text-muted-foreground` en vez de
        // `text-text-tertiary`) para lo que en la rc.1 era una sola oración —
        // se vuelve a esa oración única, con Empty/EmptyHeader/EmptyTitle
        // (ReUI) pero pisando la tipografía completa para que sea la MISMA
        // clase que antes, no una nueva.
        <Empty className="gap-1 px-4 py-[30px]">
          <EmptyHeader className="gap-0">
            <EmptyTitle className="font-sans text-xs font-medium tracking-normal text-text-tertiary">
              {puedeEscribir
                ? 'Sin actividad todavía. Registrá el primer paso con «+ Paso» — un comentario también es un paso.'
                : 'Sin actividad todavía.'}
            </EmptyTitle>
          </EmptyHeader>
        </Empty>
      ) : (
        // Sin `value`/`defaultValue`: esto no es un stepper de progreso (el
        // hilo es cronológico, no secuencial) — sin controlar el "paso
        // activo" ningún TimelineItem queda `data-completed`. El conector es
        // siempre gris neutro (`bg-border-subtle` en CLASE_SEPARADOR,
        // paridad con la rc.1), nunca el tono sólido que ReUI pinta por
        // defecto para el tramo completado.
        <Timeline role="list" aria-label="Historial de actividad" className="min-h-0 flex-1 overflow-y-auto px-4 pb-[70px] pt-1">
          {items.map((item, index) => {
            const step = index + 1;

            if (item.t === 'comentario' && item.id === editandoId) {
              return (
                <TimelineItem key={`comentario-edit-${item.id}`} step={step} id={`act-${itemKey(item)}`} role="listitem" className={CLASE_ITEM}>
                  <TimelineIndicator className={CLASE_INDICADOR}>
                    <ActividadAvatar nombre={item.autor} fotoUrl={item.fotoUrl} />
                  </TimelineIndicator>
                  <TimelineSeparator className={CLASE_SEPARADOR} />
                  <TimelineContent className="rounded-[10px] px-2.5 py-[9px]">
                    <Textarea
                      value={editTexto}
                      onChange={e => setEditTexto(e.target.value)}
                      rows={3}
                      aria-label="Editar comentario"
                      className="w-full bg-surface-card text-[13px]"
                      autoFocus
                    />
                    <div className="mt-1.5 flex justify-end gap-1.5">
                      <Button type="button" variant="outline" size="sm" onClick={() => setEditandoId(null)} className="px-2.5 text-[11.5px]">
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleGuardarEdicion}
                        disabled={actualizarComentario.isPending || !editTexto.trim()}
                        className="px-2.5 text-[11.5px]"
                      >
                        Guardar
                      </Button>
                    </div>
                  </TimelineContent>
                </TimelineItem>
              );
            }

            if (item.t === 'monto' && item.id === editandoMontoId) {
              return (
                <TimelineItem key={`monto-edit-${item.id}`} step={step} id={`act-${itemKey(item)}`} role="listitem" className={CLASE_ITEM}>
                  <TimelineIndicator className={CLASE_INDICADOR}>
                    {/* 22px con glifo de 12px, misma paridad que la fila principal (ver nota de CLASE_INDICADOR). */}
                    <IconTile
                      variant="soft"
                      size="xs"
                      className={cn(ICON_TINT.monto, '[--icon-tile-size:22px] [--icon-tile-icon-size:12px]')}
                      aria-hidden="true"
                    >
                      <BadgeDollarSign strokeWidth={2.2} />
                    </IconTile>
                  </TimelineIndicator>
                  <TimelineSeparator className={CLASE_SEPARADOR} />
                  <TimelineContent className="rounded-[10px] px-2.5 py-[9px]">
                    <MontoEditForm obra={obra} rdtId={rdt.id} item={item} onDone={() => setEditandoMontoId(null)} />
                  </TimelineContent>
                </TimelineItem>
              );
            }

            if (item.t === 'referencia' && item.id === editandoReferenciaId) {
              return (
                <TimelineItem key={`referencia-edit-${item.id}`} step={step} id={`act-${itemKey(item)}`} role="listitem" className={CLASE_ITEM}>
                  <TimelineIndicator className={CLASE_INDICADOR}>
                    {/* 22px con glifo de 12px, misma paridad que la fila principal (ver nota de CLASE_INDICADOR). */}
                    <IconTile
                      variant="soft"
                      size="xs"
                      className={cn(ICON_TINT.referencia, '[--icon-tile-size:22px] [--icon-tile-icon-size:12px]')}
                      aria-hidden="true"
                    >
                      <FileText strokeWidth={2.2} />
                    </IconTile>
                  </TimelineIndicator>
                  <TimelineSeparator className={CLASE_SEPARADOR} />
                  <TimelineContent className="rounded-[10px] px-2.5 py-[9px]">
                    <ReferenciaEditForm obra={obra} rdtId={rdt.id} item={item} onDone={() => setEditandoReferenciaId(null)} />
                  </TimelineContent>
                </TimelineItem>
              );
            }

            // Autoría real de montos/referencias: el ActividadItem solo trae
            // el NOMBRE (item.autor), no el id — se resuelve acá contra el
            // rdt completo (rdt.montos/rdt.referencias por id) para poder
            // aplicar puedeEditarPaso().
            const autorIdPaso =
              item.t === 'monto'
                ? (rdt.montos.find(m => m.id === item.id)?.autor?.id ?? null)
                : item.t === 'referencia'
                  ? (rdt.referencias.find(r => r.id === item.id)?.autor?.id ?? null)
                  : null;

            // Archivada: solo lectura en TODO el hilo, comentarios incluidos
            // (el backend ya rechaza cualquier escritura con 422).
            const puedeEditarItem = rdt.archivada
              ? false
              : item.t === 'comentario'
                ? item.autorId === currentUserId
                : item.t === 'monto' || item.t === 'referencia'
                  ? puedeEscribir && puedeEditarPaso(autorIdPaso)
                  : false;
            const puedeBorrarItem = rdt.archivada
              ? false
              : item.t === 'comentario'
                ? !item.borrado && (item.autorId === currentUserId || esAdmin)
                : item.t === 'monto' || item.t === 'referencia'
                  ? // Los pasos históricos (autor null) NUNCA se borran — el
                    // backend lo prohíbe con 403 incluso para admin.
                    puedeEscribir && autorIdPaso !== null && puedeEditarPaso(autorIdPaso)
                  : false;
            // Deshacer (punto 11a v0.9.0): solo el ítem 'hito' que ES el
            // último completado del catálogo, y solo con permiso de
            // escritura (`puedeEscribir` ya excluye archivada).
            const puedeDeshacerItem = item.t === 'hito' && puedeEscribir && ultimoHito?.codigo === item.codigo;
            const clave = itemKey(item);
            const highlightPhase = highlight?.clave === clave ? highlight.phase : null;

            return (
              <ActividadItemRow
                key={`${clave}-${item.ts}`}
                item={item}
                rdtId={rdt.id}
                rdtNro={rdt.nro}
                migrada={rdt.migrada}
                puedeEditar={puedeEditarItem}
                puedeBorrar={puedeBorrarItem}
                puedeDeshacer={puedeDeshacerItem}
                step={step}
                domId={`act-${clave}`}
                highlightPhase={highlightPhase}
                onEditar={() => {
                  if (item.t === 'comentario') {
                    // Un comentario borrado (tombstone, texto null) no se
                    // edita — guard requerido por el contrato de la API.
                    if (item.texto === null) return;
                    setEditandoId(item.id);
                    setEditTexto(item.texto);
                  } else if (item.t === 'monto') {
                    setEditandoMontoId(item.id);
                  } else if (item.t === 'referencia') {
                    setEditandoReferenciaId(item.id);
                  }
                }}
                onBorrar={() => {
                  if (item.t === 'comentario') pedirBorrarComentario(item);
                  else if (item.t === 'monto') pedirBorrarMonto(item);
                  else if (item.t === 'referencia') pedirBorrarReferencia(item);
                }}
                onDeshacer={() => {
                  if (item.t === 'hito') setDeshacerTarget({ codigo: item.codigo, nombre: item.nombre });
                }}
              />
            );
          })}
        </Timeline>
      )}

      <ConfirmDialog state={confirmState} onOpenChange={open => !open && setConfirmState(null)} />
      <DeshacerHitoDialog cc={obra.cc} rdtId={rdt.id} target={deshacerTarget} onClose={() => setDeshacerTarget(null)} />
    </>
  );

  // Drawer <1280px: Sheet modal (c-sheet-3) — el mismo `contenido`, con
  // título accesible (sr-only: la cabecera visible ya dice "Actividad").
  if (esDrawer) {
    return (
      <Sheet
        open={mobileOpen}
        onOpenChange={open => {
          if (!open) onMobileClose();
        }}
      >
        <SheetContent side="right" className="w-[min(390px,92vw)] gap-0 p-0 sm:max-w-none">
          <SheetHeader className="sr-only">
            <SheetTitle>Actividad</SheetTitle>
            <SheetDescription>Hilo de pasos, chequeos y comentarios de la redeterminación</SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col">{contenido}</div>
        </SheetContent>
      </Sheet>
    );
  }

  // ≥1280px: columna fija de siempre, sin overlay ni Sheet.
  return (
    <section
      aria-label="Actividad de la redeterminación"
      // Tiers >1440px (Juan + panel UX de 4 lentes, 2026-09-15): Actividad
      // medía lo mismo a 1441px que a 2560px y su % del viewport caía en
      // pantallas anchas — mismo mecanismo de variante arbitraria que el
      // max-[1440px]: de acá abajo, sin clamp/calc/vw. Tope 480px ≈ 65-70
      // caracteres por renglón a 12,5px, rango alto de lectura cómoda.
      className="flex min-h-0 w-[356px] flex-none flex-col border-l border-border-subtle bg-surface-page max-[1440px]:w-[312px] min-[1680px]:w-[400px] min-[1920px]:w-[448px] min-[2560px]:w-[480px]"
    >
      {contenido}
    </section>
  );
}
